<!--
  新版首页头部（双行）
  - 第一行：左侧 iconfont logo + 右侧周报入口 + iconfont 头像（"我的"入口）
  - 第二行：可选菜单按钮（仅智能助手激活时显示）+ 三个 Tab + 活动胶囊（跟随激活态滑动）
  - 设计稿尺寸基于 750 设计稿（px 单位）
  - 选中态背景由浮动胶囊（.active-pill）绘制，胶囊几何（位置/尺寸）实时测量自激活态 .tab-item，
    保证胶囊宽高与 item 完全贴合（含 padding），切换时 transform/width/height 平滑过渡
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { statusBarHeight } from '@/utils/keep-alive-config'
import MsgEyeClose from '@/assets/images/message/message-eye-close.png'
import MsgEyeOpen from '@/assets/images/message/message-eye-open.png'
import MsgEyeCloseRed from '@/assets/images/message/message-eye-close-red.png'
import MsgEyeOpenRed from '@/assets/images/message/message-eye-open-red.png'
import MsgPopupBg from '@/assets/images/message/message-popup-bg.png'
import MsgPopupTextBg from '@/assets/images/message/message-popup-text-bg.png'

defineOptions({
  name: 'NavHeader',
})

export interface NavTab {
  title: string
  path: string
  /** iconfont 类名（不含 iconfont 前缀），如 'qx-QX-zhangdan'；仅选中态显示 */
  icon?: string
}

interface Props {
  /** 当前激活 tab 索引 */
  modelValue: number
  /** Tab 配置 */
  tabs: NavTab[]
  /** 各 Tab 是否显示红点，与 tabs 下标对应 */
  redDots?: boolean[]
  /** 是否显示左侧菜单按钮（仅智能助手激活时为 true） */
  showMenu?: boolean
  /** 是否显示右上角「我的」头像入口 */
  showMine?: boolean
  /** 是否显示消息中心入口和新消息气泡 */
  showMessageEntry?: boolean
  /** 消息中心是否存在未读消息 */
  hasUnreadMessage?: boolean
  /** 首页新消息气泡是否展示 */
  messageBubbleVisible?: boolean
  /** 气泡类型：周报 / 月报 */
  messageBubbleType?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  showMenu: false,
  showMine: true,
  showMessageEntry: true,
  hasUnreadMessage: false,
  messageBubbleVisible: false,
  messageBubbleType: '周报',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
  (e: 'change', value: number): void
  (e: 'menu-click'): void
  (e: 'mine-click'): void
  (e: 'message-click'): void
  (e: 'message-bubble-close'): void
  (e: 'message-bubble-click'): void
}>()

// ==================== Tab 滑动胶囊 ====================

const trackRef = ref<HTMLElement | null>(null)
const tabRefs = ref<HTMLElement[]>([])
const setTabRef = (el: Element | null, index: number) => {
  if (el instanceof HTMLElement) tabRefs.value[index] = el
}

const pillLeft = ref(0)
const pillTop = ref(0)
const pillWidth = ref(0)
const pillHeight = ref(0)
/** 首帧禁用过渡，避免初始位置从 0 滑过去的视觉抖动 */
const pillReady = ref(false)

const pillStyle = computed(() => ({
  transform: `translate3d(${pillLeft.value}px, ${pillTop.value}px, 0)`,
  width: `${pillWidth.value}px`,
  height: `${pillHeight.value}px`,
  transition: pillReady.value
    ? 'transform .28s cubic-bezier(.4,0,.2,1), width .28s cubic-bezier(.4,0,.2,1), height .28s cubic-bezier(.4,0,.2,1)'
    : 'none',
  opacity: pillWidth.value > 0 ? 1 : 0,
}))

const measurePill = () => {
  const idx = props.modelValue
  const trackEl = trackRef.value
  const el = tabRefs.value[idx]
  if (!trackEl || !el) return
  const trackRect = trackEl.getBoundingClientRect()
  const tabRect = el.getBoundingClientRect()
  pillLeft.value = tabRect.left - trackRect.left
  pillTop.value = tabRect.top - trackRect.top
  pillWidth.value = tabRect.width
  pillHeight.value = tabRect.height
}

// 激活态变化时重新测量
watch(
  () => props.modelValue,
  async () => {
    await nextTick()
    measurePill()
  },
)

// Tab 数量 / 内容变化时（如灰度切换）重新测量
watch(
  () => props.tabs.map((t) => t.path + '|' + (t.icon || '')).join(','),
  async () => {
    await nextTick()
    measurePill()
  },
)

// 监听容器尺寸变化（横竖屏 / 系统字号）
useResizeObserver(trackRef, () => {
  measurePill()
})

// ==================== 事件 ====================

const handleTabClick = (index: number) => {
  if (index !== props.modelValue) {
    emit('update:modelValue', index)
  }
  emit('change', index)
}

const handleMenuClick = () => emit('menu-click')
const handleMineClick = () => emit('mine-click')

// ==================== 消息中心入口提示层 ====================

const messageEyeOpen = ref(false)
let messageEyeTimer: number | undefined
const MESSAGE_EYE_CLOSE_DURATION = 600
const MESSAGE_EYE_OPEN_DURATION = 1400

const messageEyeIcon = computed(() => {
  if (props.hasUnreadMessage) {
    return messageEyeOpen.value ? MsgEyeOpenRed : MsgEyeCloseRed
  }
  return messageEyeOpen.value ? MsgEyeOpen : MsgEyeClose
})
const messagePopupStyle = computed(() => ({
  backgroundImage: `url(${MsgPopupBg})`,
}))
const messagePopupTextStyle = computed(() => ({
  backgroundImage: `url(${MsgPopupTextBg})`,
}))

const handleMessageEntryClick = () => emit('message-click')
const handleMessagePopupContentClick = () => emit('message-bubble-click')
const closeMessagePopup = () => emit('message-bubble-close')

const scheduleMessageEyeToggle = () => {
  const duration = messageEyeOpen.value ? MESSAGE_EYE_OPEN_DURATION : MESSAGE_EYE_CLOSE_DURATION
  messageEyeTimer = window.setTimeout(() => {
    messageEyeTimer = undefined
    messageEyeOpen.value = !messageEyeOpen.value
    scheduleMessageEyeToggle()
  }, duration)
}

const clearMessageEyeTimer = () => {
  if (!messageEyeTimer) return
  window.clearTimeout(messageEyeTimer)
  messageEyeTimer = undefined
}

onMounted(async () => {
  await nextTick()
  measurePill()
  // 下一帧再开启过渡
  requestAnimationFrame(() => {
    pillReady.value = true
  })
  scheduleMessageEyeToggle()
})

onBeforeUnmount(() => {
  clearMessageEyeTimer()
})

// ==================== 状态栏让位（App 环境） ====================

/** 顶部 padding-top（App 状态栏让位；H5 为 0） */
const headerStyle = computed(() => ({
  paddingTop: `${statusBarHeight.value}px`,
}))

// 状态栏高度变化时（首次注入后），tab 行偏移会变 → 重新测量胶囊
watch(statusBarHeight, async () => {
  await nextTick()
  measurePill()
})
</script>

<template>
  <header class="nav-header" :style="headerStyle">
    <!-- 第一行：logo + 周报入口 + 头像入口 -->
    <div class="nav-header__top">
      <i class="logo-icon iconfont qx-zhinengzhushou" />
      <div v-if="showMine" class="nav-header__actions">
        <div v-if="showMessageEntry" class="weekly-entry-wrap">
          <Transition name="weekly-popup">
            <div
              v-if="messageBubbleVisible"
              id="message-popup"
              class="weekly-popup"
              :style="messagePopupStyle"
            >
              <button
                class="weekly-popup__content"
                type="button"
                :style="messagePopupTextStyle"
                @click="handleMessagePopupContentClick"
              >
                <span class="weekly-popup__title">点击查看{{ messageBubbleType }}</span>
              </button>
              <button
                class="weekly-popup__close"
                type="button"
                aria-label="关闭消息提示"
                @click.stop="closeMessagePopup"
              >
                <span class="weekly-popup__close-text">关闭</span>
                <i class="iconfont qx-guanbi weekly-popup__close-icon" />
              </button>
            </div>
          </Transition>
          <button
            class="weekly-entry"
            type="button"
            aria-label="消息中心"
            aria-controls="message-popup"
            :aria-expanded="messageBubbleVisible"
            @click="handleMessageEntryClick"
          >
            <img class="weekly-entry__icon" :src="messageEyeIcon" alt="" />
          </button>
        </div>
        <button class="mine-entry" type="button" aria-label="我的" @click="handleMineClick">
          <i class="mine-icon iconfont qx-QX-my0630" />
        </button>
      </div>
    </div>

    <!-- 第二行：菜单 + Tab + 活动胶囊 -->
    <div class="nav-header__tabs">
      <Transition name="menu-slide">
        <button
          v-if="showMenu"
          class="menu-btn"
          type="button"
          aria-label="菜单"
          @click="handleMenuClick"
        >
          <i class="iconfont qx-zuoduiqi" />
        </button>
      </Transition>

      <div ref="trackRef" class="tabs-track" :class="{ 'tabs-track--menu-visible': showMenu }">
        <div class="active-pill" :style="pillStyle" />
        <div
          v-for="(tab, idx) in tabs"
          :key="tab.path"
          :ref="(el) => setTabRef(el as Element | null, idx)"
          class="tab-item"
          :class="{ active: idx === modelValue }"
          @click="handleTabClick(idx)"
        >
          <span v-if="tab.icon" class="tab-icon" :class="{ visible: idx === modelValue }">
            <i :class="['iconfont', tab.icon]" />
          </span>
          <span class="tab-title">{{ tab.title }}</span>
          <TabRedDot v-if="redDots && redDots[idx]" />
        </div>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.nav-header {
  // 作为 .app-root 的 flex column 第一个子元素，占自然高度（状态栏 padding-top + 88+88px）；
  // position: relative + z-index 保证 NavHeader 始终位于业务页面上层，避免被 absolute 子元素覆盖
  position: relative;
  flex-shrink: 0;
  width: 100%;
  z-index: 99;
  box-sizing: border-box;
}

// PC 画布固定为 375px 逻辑宽度。UAT 合并分支中的桌面媒体规则使用 rem，
// 会在真实浏览器根字号下覆盖 viewport 适配值，因此在公共头部层显式恢复逻辑尺寸。
:global(html.qx-pc-canvas .nav-header .nav-header__top) {
  height: 44px;
  padding: 0 16px 0 18px;
}

:global(html.qx-pc-canvas .nav-header .nav-header__tabs) {
  height: 44px;
  padding: 6px 16px;
}

:global(html.qx-pc-canvas .nav-header__tabs .tabs-track) {
  height: 32px;
  gap: 8px;
}

:global(html.qx-pc-canvas .nav-header__tabs .tabs-track .tab-item) {
  padding: 6px 12px;
  font-size: 15px;
}

:global(html.qx-pc-canvas .nav-header__tabs .menu-btn) {
  top: 10px;
  width: 24px;
  height: 24px;
}

:global(html.qx-pc-canvas .nav-header__tabs .menu-btn i) {
  font-size: 24px;
  line-height: 24px;
}

// 第一行
.nav-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 88px;
  padding: 0 32px 0 36px;

  // logo（默认纯色，不渐变染色）
  .logo-icon {
    display: inline-block;
    width: 144px;
    height: 36px;
    font-size: 36px;
    line-height: 36px;
    color: #3155ef;
  }

  .nav-header__actions {
    display: flex;
    align-items: flex-start;
    height: 52px;
  }

  .weekly-entry-wrap {
    position: relative;
    width: 52px;
    height: 52px;
    margin-right: 16px;
    flex-shrink: 0;
  }

  .weekly-entry {
    position: relative;
    width: 52px;
    height: 52px;
    padding: 0;
    border: none;
    line-height: 1;
    background: transparent;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:active {
      opacity: 0.8;
    }
  }

  .weekly-entry__icon {
    display: block;
    width: 52px;
    height: 52px;
  }

  .weekly-popup {
    position: absolute;
    top: 50%;
    right: calc(100% + 8px);
    z-index: 3;
    width: 271px;
    height: 50px;
    padding: 1px 19px 1px 1px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    background-position: center;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    transform: translate3d(0, -50%, 0) scale(1);
    transform-origin: right center;
  }

  .weekly-popup__content,
  .weekly-popup__close {
    padding: 0;
    border: none;
    background-color: transparent;
    cursor: pointer;
  }

  .weekly-popup__content {
    width: 173px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-position: center;
    background-size: 100% 100%;
    background-repeat: no-repeat;
  }

  .weekly-popup__title {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 22px;
    font-family:
      PingFangSC-Medium,
      PingFang SC,
      sans-serif;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
  }

  .weekly-popup__close {
    height: 48px;
    margin-left: 17px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6f7886;
  }

  .weekly-popup__close-text {
    display: flex;
    align-items: center;
    font-size: 22px;
    font-weight: 400;
    line-height: 1;
    white-space: nowrap;
  }

  .weekly-popup__close-icon {
    width: 16px;
    height: 16px;
    margin-left: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    color: #6f7886;
  }

  .weekly-popup-enter-from,
  .weekly-popup-leave-to {
    opacity: 0;
    transform: translate3d(14px, -50%, 0) scale(0.92);
  }

  .weekly-popup-enter-to,
  .weekly-popup-leave-from {
    opacity: 1;
    transform: translate3d(0, -50%, 0) scale(1);
  }

  .weekly-popup-enter-active,
  .weekly-popup-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.26s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .mine-entry {
    width: 52px;
    height: 52px;
    padding: 0;
    border: none;
    line-height: 1;
    background: transparent;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    // iconfont 渐变染色：background-clip:text 方案
    .mine-icon {
      display: inline-block;
      width: 52px;
      height: 52px;
      font-size: 52px;
      line-height: 52px;
      background: linear-gradient(45deg, #3155ef 0%, #9372ff 100%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      transform: translate3d(0, 2px, 0);
    }

    &:active {
      opacity: 0.8;
    }
  }
}

// 第二行
.nav-header__tabs {
  height: 88px;
  padding: 12px 32px;
  position: relative;
  overflow: hidden;
}

// 菜单按钮（智能助手专属）
.menu-btn {
  position: absolute;
  left: 32px;
  top: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  overflow: hidden;
  z-index: 2;

  i {
    font-size: 48px;
    line-height: 48px;
    color: #141c28;
  }

  &:active {
    opacity: 0.7;
  }
}

// 菜单按钮进出过渡：只做合成动画，不改变 Tab 布局
.menu-slide-enter-from,
.menu-slide-leave-to {
  opacity: 0;
  transform: translate3d(-12px, 0, 0);
}
.menu-slide-enter-to,
.menu-slide-leave-from {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}
.menu-slide-enter-active,
.menu-slide-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

// Tab 轨道
// gap:16px 推导：选中胶囊边到隔壁 item 文字 = gap(16) + 隔壁 padding-left(24) = 40px
.tabs-track {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  min-width: 0;
  height: 64px;
  transform: translate3d(0, 0, 0);
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;

  &.tabs-track--menu-visible {
    transform: translate3d(72px, 0, 0);
  }

  // 浮动胶囊：宽高/位置由 measurePill 实时测量，与激活态 .tab-item 完全贴合
  .active-pill {
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 31px;
    background: rgba(49, 85, 239, 0.08);
    box-sizing: border-box;
    pointer-events: none;
    will-change: transform, width, height;
  }

  .tab-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    font-size: 30px;
    font-weight: 400;
    color: #141c28;
    line-height: 1;
    border-radius: 31px;
    white-space: nowrap;
    position: relative;
    cursor: pointer;
    transition: color 0.2s ease;

    // 激活态图标：默认 width:0 不占 layout，加上 .visible 后展开。
    // width / margin-right 走"瞬变"（不参与 transition），保证 modelValue 切换后
    // 同一 tick 内 layout 就稳定到目标宽度，measurePill 能拿到准确值，避免胶囊覆盖错位。
    .tab-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 0;
      margin-right: 0;
      opacity: 0;
      overflow: hidden;
      transition: opacity 0.22s ease;

      i {
        display: inline-block;
        width: 40px;
        height: 40px;
        font-size: 40px;
        line-height: 40px;
        background: linear-gradient(225deg, #9372ff 0%, #3155ef 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
      }

      // 智能助手图标渐变方向不同（0deg #7C5FF8 → #3155EF）
      i.qx-QX-zhinengzhushou {
        background: linear-gradient(180deg, #7c5ff8 0%, #3155ef 100%);
        -webkit-background-clip: text;
        background-clip: text;
      }

      &.visible {
        width: 40px;
        margin-right: 4px;
        opacity: 1;
      }
    }

    .tab-title {
      display: inline-block;
    }

    &.active {
      color: #141c28;
    }

    &:active {
      opacity: 0.85;
    }
  }
}

@media (min-width: 900px) {
  .nav-header__top { height: 4rem; padding: 0 2rem; }
  .nav-header__tabs { height: 4rem; padding: 0.5rem 2rem; }
  .tabs-track { height: 3rem; gap: 1rem; }
  .tabs-track .tab-item { padding: 0.6rem 1.2rem; font-size: 1.25rem; }
  .menu-btn { top: 0.5rem; width: 3rem; height: 3rem; }
  .menu-btn i { font-size: 2rem; line-height: 2rem; }
}
</style>
